import React, { useState } from "react";
import { Modal, Button, Input } from "antd";
import {
  UploadOutlined,
  FileExcelOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  DeleteOutlined,
  LockOutlined,
} from "@ant-design/icons";
import axios from "axios";
import LoadingSpinner from "../loaders/LoadingSpinner";

export default function ImportUsersModal({
  isOpen,
  onClose,
  client,
  clientId,
  onSuccess,
}) {
  const [file, setFile] = useState(null);
  const [defaultPassword, setDefaultPassword] = useState("password");
  const [isUploading, setIsUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  const clientName = client?.CustName || client?.name || client?.company_name || "Client";

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      validateAndSetFile(selectedFile);
    }
  };

  const validateAndSetFile = (selectedFile) => {
    const validExtensions = [".csv", ".xlsx", ".xls"];
    const fileName = selectedFile.name.toLowerCase();
    const isValid = validExtensions.some((ext) => fileName.endsWith(ext));

    if (!isValid) {
      setErrorMessage("Please select a valid CSV or Excel file (.csv, .xlsx, .xls)");
      return;
    }

    setFile(selectedFile);
    setErrorMessage(null);
    setResult(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) {
      validateAndSetFile(droppedFile);
    }
  };

  const handleRemoveFile = () => {
    setFile(null);
    setResult(null);
    setErrorMessage(null);
  };

  const handleModalClose = () => {
    if (isUploading) return;
    setFile(null);
    setDefaultPassword("password");
    setResult(null);
    setErrorMessage(null);
    onClose();
  };

  const handleImportSubmit = async () => {
    if (!file) {
      setErrorMessage("Please select a file to upload.");
      return;
    }

    setIsUploading(true);
    setErrorMessage(null);
    setResult(null);

    try {
      const token = localStorage.getItem("superAdminToken");
      if (!token) {
        setErrorMessage("SuperAdmin authentication token not found. Please log in again.");
        setIsUploading(false);
        return;
      }

      const formData = new FormData();
      formData.append("file", file);
      formData.append("default_password", defaultPassword || "password");

      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/super-admin/client-management/client/${clientId}/import-users`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = response.data?.payload || response.data?.data || response.data;
      setResult(data);

      if (onSuccess) {
        onSuccess(response.data?.message || "Employees imported successfully!");
      }
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to import employee records.";
      setErrorMessage(msg);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Modal
      title={
        <div style={{ paddingBottom: "8px", borderBottom: "1px solid #f0f0f0" }}>
          <h5 style={{ margin: 0, fontWeight: "600", color: "#111827" }}>
            Import Employee Records
          </h5>
          <span style={{ fontSize: "13px", color: "#6b7280" }}>
            Target Client: <strong>{clientName}</strong>
          </span>
        </div>
      }
      open={isOpen}
      onCancel={handleModalClose}
      footer={null}
      width={600}
      centered
      destroyOnClose
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "16px", marginTop: "12px" }}>
        {/* Informational Banner */}
        <div
          style={{
            backgroundColor: "#f0fdf4",
            border: "1px solid #bbf7d0",
            borderRadius: "8px",
            padding: "12px 14px",
            fontSize: "12.5px",
            color: "#166534",
            lineHeight: "1.5",
          }}
        >
          <div style={{ display: "flex", alignItems: "flex-start", gap: "8px" }}>
            <ExclamationCircleOutlined style={{ fontSize: "16px", marginTop: "2px", color: "#15803d" }} />
            <div>
              <strong>Tenant Scoped Import:</strong> Records will be imported exclusively into{" "}
              <strong>{clientName}</strong>'s database. System administrator accounts (
              <code>000000</code>) are automatically excluded, contact information is encrypted, and
              manager hierarchies are linked automatically.
            </div>
          </div>
        </div>

        {/* File Dropzone / Selector */}
        {!result && (
          <div>
            <label style={{ display: "block", marginBottom: "6px", fontWeight: "500", fontSize: "13px", color: "#374151" }}>
              Upload Employee Export File (.csv, .xlsx, .xls) <span style={{ color: "red" }}>*</span>
            </label>

            {!file ? (
              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                style={{
                  border: isDragging ? "2px dashed #2563eb" : "2px dashed #d1d5db",
                  backgroundColor: isDragging ? "#eff6ff" : "#f9fafb",
                  borderRadius: "8px",
                  padding: "24px 16px",
                  textAlign: "center",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                }}
                onClick={() => document.getElementById(`import-file-input-${clientId}`)?.click()}
              >
                <input
                  id={`import-file-input-${clientId}`}
                  type="file"
                  accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                  style={{ display: "none" }}
                  onChange={handleFileChange}
                />
                <FileExcelOutlined style={{ fontSize: "36px", color: "#16a34a", marginBottom: "8px" }} />
                <p style={{ margin: "0 0 4px", fontWeight: "500", color: "#374151", fontSize: "14px" }}>
                  Click to browse or drag & drop exported file here
                </p>
                <span style={{ fontSize: "12px", color: "#9ca3af" }}>
                  Supports exported CSV or Excel sheets from legacy ssl_cloud_app
                </span>
              </div>
            ) : (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "12px 14px",
                  backgroundColor: "#f3f4f6",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <FileExcelOutlined style={{ fontSize: "24px", color: "#16a34a" }} />
                  <div>
                    <div style={{ fontWeight: "500", fontSize: "13.5px", color: "#111827" }}>
                      {file.name}
                    </div>
                    <span style={{ fontSize: "11.5px", color: "#6b7280" }}>
                      {(file.size / 1024).toFixed(1)} KB
                    </span>
                  </div>
                </div>
                {!isUploading && (
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={handleRemoveFile}
                    title="Remove file"
                  />
                )}
              </div>
            )}
          </div>
        )}

        {/* Default Password Field */}
        {!result && (
          <div>
            <label style={{ display: "block", marginBottom: "6px", fontWeight: "500", fontSize: "13px", color: "#374151" }}>
              Default Password for Imported Users
            </label>
            <Input.Password
              prefix={<LockOutlined style={{ color: "#9ca3af" }} />}
              value={defaultPassword}
              onChange={(e) => setDefaultPassword(e.target.value)}
              placeholder="Enter default password"
              disabled={isUploading}
            />
            <span style={{ fontSize: "11.5px", color: "#6b7280", marginTop: "4px", display: "block" }}>
              All imported employees will be able to log in using this initial password and change it afterwards.
            </span>
          </div>
        )}

        {/* Error Message */}
        {errorMessage && (
          <div
            style={{
              backgroundColor: "#fef2f2",
              border: "1px solid #fecaca",
              borderRadius: "6px",
              padding: "10px 12px",
              display: "flex",
              alignItems: "flex-start",
              gap: "8px",
              color: "#991b1b",
              fontSize: "13px",
            }}
          >
            <CloseCircleOutlined style={{ marginTop: "2px", color: "#dc2626" }} />
            <span>{errorMessage}</span>
          </div>
        )}

        {/* Result Summary */}
        {result && (
          <div
            style={{
              backgroundColor: "#f8fafc",
              border: "1px solid #e2e8f0",
              borderRadius: "8px",
              padding: "16px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px", color: (result.failures && result.failures.length > 0) ? "#d97706" : "#16a34a" }}>
              {(result.failures && result.failures.length > 0) ? (
                <ExclamationCircleOutlined style={{ fontSize: "20px", color: "#d97706" }} />
              ) : (
                <CheckCircleOutlined style={{ fontSize: "20px", color: "#16a34a" }} />
              )}
              <strong style={{ fontSize: "15px" }}>
                {(result.failures && result.failures.length > 0)
                  ? "Import Completed with Notices / Failures"
                  : "Import Completed Successfully!"}
              </strong>
            </div>

            {/* 4-Card Summary Grid */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "8px", marginBottom: "14px" }}>
              <div style={{ backgroundColor: "#ffffff", padding: "10px 8px", borderRadius: "6px", border: "1px solid #e5e7eb", textAlign: "center" }}>
                <span style={{ fontSize: "10.5px", color: "#6b7280", textTransform: "uppercase", fontWeight: "600" }}>Imported</span>
                <div style={{ fontSize: "20px", fontWeight: "700", color: "#16a34a" }}>
                  {result.success_count ?? 0}
                </div>
              </div>
              <div style={{ backgroundColor: "#ffffff", padding: "10px 8px", borderRadius: "6px", border: "1px solid #e5e7eb", textAlign: "center" }}>
                <span style={{ fontSize: "10.5px", color: "#6b7280", textTransform: "uppercase", fontWeight: "600" }}>Managers Linked</span>
                <div style={{ fontSize: "20px", fontWeight: "700", color: "#2563eb" }}>
                  {result.managers_linked ?? 0}
                </div>
              </div>
              <div style={{ backgroundColor: "#ffffff", padding: "10px 8px", borderRadius: "6px", border: "1px solid #e5e7eb", textAlign: "center" }}>
                <span style={{ fontSize: "10.5px", color: "#6b7280", textTransform: "uppercase", fontWeight: "600" }}>Failed</span>
                <div style={{ fontSize: "20px", fontWeight: "700", color: (result.failures?.length > 0 ? "#dc2626" : "#64748b") }}>
                  {result.failures?.length ?? 0}
                </div>
              </div>
              <div style={{ backgroundColor: "#ffffff", padding: "10px 8px", borderRadius: "6px", border: "1px solid #e5e7eb", textAlign: "center" }}>
                <span style={{ fontSize: "10.5px", color: "#6b7280", textTransform: "uppercase", fontWeight: "600" }}>Admin Skipped</span>
                <div style={{ fontSize: "20px", fontWeight: "700", color: "#64748b" }}>
                  {result.skipped_count ?? 0}
                </div>
              </div>
            </div>

            {/* Detailed Failure & Reason Breakdown */}
            {result.failures && result.failures.length > 0 ? (
              <div style={{ backgroundColor: "#fef2f2", border: "1px solid #fecaca", borderRadius: "6px", padding: "12px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
                  <span style={{ fontSize: "12px", fontWeight: "600", color: "#991b1b" }}>
                    Failed Records Breakdown & Reason ({result.failures.length}):
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText(result.failures.join("\n"));
                      alert("Error log copied to clipboard!");
                    }}
                    style={{
                      background: "none",
                      border: "none",
                      color: "#b91c1c",
                      fontSize: "11px",
                      cursor: "pointer",
                      textDecoration: "underline",
                      padding: 0,
                    }}
                  >
                    Copy Error Log
                  </button>
                </div>
                <div
                  style={{
                    maxHeight: "140px",
                    overflowY: "auto",
                    backgroundColor: "#ffffff",
                    border: "1px solid #fee2e2",
                    borderRadius: "4px",
                    padding: "8px",
                    fontSize: "11.5px",
                    color: "#7f1d1d",
                    fontFamily: "monospace",
                  }}
                >
                  {result.failures.map((failure, idx) => (
                    <div key={idx} style={{ padding: "3px 0", borderBottom: idx < result.failures.length - 1 ? "1px dashed #fee2e2" : "none" }}>
                      • {failure}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div style={{ fontSize: "12px", color: "#15803d", backgroundColor: "#f0fdf4", border: "1px solid #bbf7d0", padding: "8px 12px", borderRadius: "6px" }}>
                ✓ All employee records were processed and imported with 0 errors.
              </div>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "10px",
            marginTop: "12px",
            paddingTop: "12px",
            borderTop: "1px solid #f0f0f0",
          }}
        >
          {result ? (
            <Button
              type="primary"
              style={{ backgroundColor: "#16a34a", borderColor: "#16a34a" }}
              onClick={handleModalClose}
            >
              Done
            </Button>
          ) : (
            <>
              <Button onClick={handleModalClose} disabled={isUploading}>
                Cancel
              </Button>
              <Button
                type="primary"
                icon={<UploadOutlined />}
                style={{ backgroundColor: "#1e3a8a", borderColor: "#1e3a8a" }}
                onClick={handleImportSubmit}
                disabled={!file || isUploading}
              >
                {isUploading ? (
                  <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <LoadingSpinner /> Importing...
                  </span>
                ) : (
                  "Import Employees"
                )}
              </Button>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}
